import socket from '@/utils/socket';
import { router } from '../../../main';
import routesConstants from '../../../router/routes.constants';
import { authenticationApi } from '../../../utils/axiosConfig';
import services from './services';
import { subscribe } from '@/utils/socket';
import { setCookie } from '../../../utils/cookies';
import {
  initGrowthbook,
  setGrowthbookAttributes
} from '../../../plugins/growthbook/growthbook';
import { setSentryUser } from '@/plugins/sentry';

async function checkIfAuthenticated(context) {
  let user;
  try {
    user = await services.fetchUser();
  } catch (error) {
    // fetchUser only throws on persistent transient failures (network
    // error, 429, 5xx) — the auth state is unknown, so keep the current
    // one instead of declaring the user logged out (which redirects to
    // login and, inside the app webview, ping-pongs against the native
    // shell's login handling).
    window.Logger.error(error);
    return context.getters['isAuthenticated'];
  }
  if (user) {
    context.commit('setAuthenticated', { isAuthenticated: true });
    context.commit('setUser', user);
    context.commit('setImpersonated', {
      isImpersonated: user.isImpersonated || false
    });
    setGrowthbookAttributes();
    // Tag Sentry events with the authenticated user id so we can drill
    // from an aggregated slow page down to a specific session. We only
    // send the id — anything else is stripped in beforeSend anyway.
    setSentryUser(user);
    return true;
  }

  context.commit('setAuthenticated', { isAuthenticated: false });
  // Clear the Sentry user on logout/unauthenticated so subsequent
  // events aren't mis-attributed to the previous session.
  setSentryUser(null);
  return false;
}

async function checkAuth(context) {
  await context.dispatch('initAppData');

  const isAuthenticated = context.getters['isAuthenticated'];
  if (
    !isAuthenticated &&
    router.currentRoute.fullPath !== routesConstants.LOGIN
  ) {
    router.replace(routesConstants.LOGIN);
  }
}

async function initAppData(context) {
  context.dispatch('screenSize/checkScreenSize', null, { root: true });
  // 1. Check if the user is authenticated
  await context.dispatch('checkIfAuthenticated');

  await initGrowthbook();

  if (!context.state.isAuthenticated) {
    // Proposition page in modal reroute
    const propositionPageId = new URL(location.href).searchParams.get(
      'propositionPageId'
    );
    if (propositionPageId) {
      location.href = `${routesConstants.PROPOSITION}/${propositionPageId}`;
      return;
    }

    window.Logger.log('>>> Not authenticated, setting appready to true <<<');
    const accessOverridable = await context.dispatch('isAccessOverridable');
    context.commit('setIsAccessOverridable', accessOverridable);

    context.commit('appReady');
    return;
  }

  socket.connect();

  context.dispatch('payment/init', null, { root: true });
  context.dispatch('interests/init', null, { root: true });
  context.dispatch('superLikes/init', null, { root: true });

  // 2. get primary proposition and minimal my propositions

  // Check if primary proposition is passed as query param
  const primary = new URL(location.href).searchParams.get('primary');

  if (primary) {
    try {
      await context.dispatch(
        'myPropositions/setPrimaryPropositionRequest',
        primary,
        {
          root: true
        }
      );
    } catch (error) {
      console.error(error);
    }
  }

  const [primaryProposition, myPropositions] = await Promise.all([
    context.dispatch('myPropositions/fetchPrimaryProposition', null, {
      root: true
    }),
    context.dispatch('myPropositions/fetchPropositions', null, { root: true })
  ]);

  context.dispatch('extraVisibility/checkPaymentRedirect', null, {
    root: true
  });
  context.dispatch('superLikes/checkPaymentRedirect', null, {
    root: true
  });

  context.commit(
    'myPropositions/setPrimaryProposition',
    {
      primaryProposition
    },
    { root: true }
  );

  // Set currently editing proposition to primary proposition
  context.commit(
    'myPropositions/setCurrentlyEditingProposition',
    {
      currentlyEditingProposition: primaryProposition
    },
    { root: true }
  );

  setGrowthbookAttributes(null, true);

  // 3. Fire app data ready, if primary proposition doesn't exists and end there
  if (!primaryProposition) {
    context.commit('appReady');
    return;
  }

  context.dispatch('swapList/getSwapInterestCounts', null, { root: true });

  // 4. set trial banner
  context.dispatch(
    'trial/setTrialBanner',
    {
      primaryPropositionId: primaryProposition.propositionId,
      propositions: myPropositions
    },
    {
      root: true
    }
  );
  context.dispatch(
    'trial/setOutdatedTrials',
    { propositions: myPropositions },
    { root: true }
  );

  // 5. Get notification center notifications
  context.dispatch('notificationCenter/init', null, { root: true });

  context.dispatch('menu/init', primaryProposition.propositionId, {
    root: true
  });
  context.dispatch('myPropositions/init', null, { root: true });

  subscribe('new-match', event => {
    context.dispatch('matches/setNewMatch', event, { root: true });
  });

  subscribe('reload-required', () => {
    location.reload();
  });

  // 7. Fire app data ready
  context.commit('appReady');

  // 8. Check if user needs to complete tasks
  const tasksToComplete = await context.dispatch(
    'fetchAndSetTasksToComplete',
    primaryProposition.propositionId
  );

  // Redirect to edit areas page if user needs to edit areas
  if (tasksToComplete?.length > 0 && tasksToComplete.includes('EDIT_AREA')) {
    context.commit('wishAreas/setMustSelectNewAreas', true, { root: true });
    router
      .push(
        `${routesConstants.EDIT_WISH_AREAS}/${primaryProposition.propositionId}/1`
      )
      .catch(err => {
        if (err.name !== 'NavigationDuplicated') throw err;
      });
  }
}

async function waitForTasksToCompleteInit(context) {
  return new Promise(resolve => {
    if (context.getters['tasksToComplete']) {
      resolve();
      return;
    }

    let waitIval = setInterval(() => {
      if (!context.getters['tasksToComplete']) {
        return;
      }

      clearInterval(waitIval);
      resolve();
    }, 50);
  });
}

async function removeImpersonation(context) {
  let isImpersonated = false;
  const dotDomain = location.hostname.replace('www', '');
  setCookie('isImpersonated', false, 0, dotDomain);

  context.commit('setImpersonated', { isImpersonated });
}

async function updateUser(context, user) {
  context.commit('updateUser', user);
}

async function isAccessOverridable() {
  try {
    await authenticationApi.get('/auth/preview');
    return true;
  } catch (error) {
    // do nothing
  }

  return false;
}

async function removeAccount() {
  try {
    await authenticationApi.post('remove');
    return true;
  } catch (error) {
    return false;
  }
}

// Drops the task from tasksToComplete straight away so whatever it gates
// hides at once. A failed request leaves it open server side, so it comes
// back on the next load.
async function completePropositionTask(context, { propositionId, task }) {
  if (!propositionId || !task) return;
  const tasksToComplete = context.getters.tasksToComplete;
  if (Array.isArray(tasksToComplete)) {
    context.commit(
      'setTasksToComplete',
      tasksToComplete.filter(t => t !== task)
    );
  }
  await services.completePropositionTask(propositionId, task);
}

async function fetchAndSetTasksToComplete(context, propositionId) {
  const tasksToComplete = await services.fetchPropositionTasks(propositionId);
  if (tasksToComplete !== null) {
    context.commit('setTasksToComplete', tasksToComplete);
  }
  return tasksToComplete;
}

export default {
  removeImpersonation,
  waitForTasksToCompleteInit,
  initAppData,
  checkIfAuthenticated,
  updateUser,
  checkAuth,
  isAccessOverridable,
  removeAccount,
  fetchAndSetTasksToComplete,
  completePropositionTask
};
